import React from "react";
import {
  FiAward,
  FiBell,
  FiCreditCard,
  FiDollarSign,
  FiFileText,
  FiRefreshCw,
  FiShield,
  FiUser,
  FiX,
  FiCheck,
  FiMail,
} from "react-icons/fi";
import defaultAvatar from "../../assets/profile.png";
import { relativeTime } from "../../utils/relativeTime";

const GROUP_LABEL = { licences: "Licence", rights: "Rights", wallet: "Wallet", account: "Account" };

// Icon by what happened, then by group; social notices show the sender's picture.
function iconFor(item) {
  const event = item.event || "";
  if (event.includes("refund")) return FiRefreshCw;
  if (event === "award") return FiAward;
  if (event.startsWith("payout") || event === "tip" || event === "earning") return FiDollarSign;
  if (event === "subscription_payment") return FiCreditCard;
  switch (item.group) {
    case "licences":
      return FiFileText;
    case "rights":
      return FiShield;
    case "wallet":
      return FiCreditCard;
    case "account":
      return FiUser;
    default:
      return FiBell;
  }
}

// One notice: icon for its type, title, one line of text, relative time and an unread dot (ticket 2.8.1).
// The whole row opens the notice; mark read/unread and dismiss are separate buttons so the keyboard can reach each one.
const NotificationItem = ({ item, onOpen, onToggleRead, onDismiss, unavailable, compact = false }) => {
  const Icon = iconFor(item);
  const unread = !item.is_read;
  const label = `${unread ? "Unread: " : ""}${item.title}`;
  return (
    <li className={`${unread ? "bg-green-50 dark:bg-gray-800" : "bg-white dark:bg-gray-900"} border-b last:border-b-0 border-gray-200 dark:border-gray-700`}>
      <div className="flex items-start gap-1 pr-2">
        <button
          type="button"
          onClick={() => onOpen(item)}
          aria-label={label}
          className="flex flex-1 min-w-0 items-start gap-3 px-3 py-3 text-left rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
        >
          <span className="relative shrink-0 mt-0.5">
            {item.group || !item.profile_image ? (
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-200" aria-hidden="true">
                <Icon className="h-5 w-5" />
              </span>
            ) : (
              <img src={item.profile_image || defaultAvatar} alt="" className="h-9 w-9 rounded-full object-cover" />
            )}
            {unread && <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-white dark:ring-gray-900" aria-hidden="true" />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-baseline justify-between gap-2">
              <span className={`block truncate text-sm ${unread ? "font-bold text-gray-900 dark:text-white" : "font-semibold text-gray-700 dark:text-gray-300"}`}>{item.title}</span>
              <span className="shrink-0 text-xs text-gray-400">{relativeTime(item.created_at)}</span>
            </span>
            <span className="mt-0.5 block text-sm text-gray-500 dark:text-gray-400 line-clamp-1">{item.message}</span>
            {!compact && item.group && <span className="mt-1 inline-block text-[11px] font-medium text-gray-500 dark:text-gray-400">{GROUP_LABEL[item.group]}</span>}
            {unavailable && (
              <span role="status" className="mt-1 block text-xs font-medium text-amber-700 dark:text-amber-400">
                {unavailable}
              </span>
            )}
          </span>
        </button>
        {!compact && (
          <span className="flex shrink-0 items-center gap-1 pt-2">
            <button
              type="button"
              onClick={() => onToggleRead(item)}
              aria-label={unread ? "Mark as read" : "Mark as unread"}
              title={unread ? "Mark as read" : "Mark as unread"}
              className="rounded-full p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
            >
              {unread ? <FiCheck className="h-4 w-4" aria-hidden="true" /> : <FiMail className="h-4 w-4" aria-hidden="true" />}
            </button>
            <button
              type="button"
              onClick={() => onDismiss(item)}
              aria-label="Dismiss notification"
              title="Dismiss"
              className="rounded-full p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
            >
              <FiX className="h-4 w-4" aria-hidden="true" />
            </button>
          </span>
        )}
      </div>
    </li>
  );
};

export default NotificationItem;
